import { useState } from "react";
import { toast } from "sonner";
import axios from "axios";
import { motion } from "framer-motion";
import { API } from "../App";
import {
  Mail,
  Phone,
  MapPin,
  Send,
  MessageSquare,
  Instagram,
  Facebook,
  Loader2,
} from "lucide-react";
import { Section, GlassCard, Eyebrow } from "../components/Section";
import NeuralNetworkBackground, { ScaleWatermark } from "../components/NeuralNetworkBackground";

const submissionTypes = [
  { v: "general", l: "General Inquiry" },
  { v: "lawyer", l: "Lawyer Onboarding" },
  { v: "ngo", l: "NGO / Donor" },
  { v: "government", l: "Government / Institutional" },
  { v: "partnership", l: "Partnership" },
  { v: "support", l: "Support / Help" },
];

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    organization: "",
    message: "",
    submission_type: "general",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Message sent successfully — we'll be in touch shortly.");
      setFormData({ name: "", email: "", organization: "", message: "", submission_type: "general" });
    } catch (error) {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const methods = [
    { icon: Mail, title: "Email", value: "info@accessjusticeglobal.org", href: "mailto:info@accessjusticeglobal.org", note: "General inquiries" },
    { icon: Phone, title: "WhatsApp", value: "+234 811 444 5557", href: "https://wa.me/2348114445557", note: "Chat directly" },
    { icon: MapPin, title: "Headquarters", value: "Built in Nigeria · Global Operations", note: "Multi-jurisdiction" },
  ];

  return (
    <div className="bg-ink text-white" data-testid="contact-page">
      <section className="relative pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0 opacity-50"><NeuralNetworkBackground density={22} /></div>
        <ScaleWatermark />
        <div className="turquoise-aura -top-32 left-0 opacity-50" />
        <div className="grain-overlay" />
        <div className="container relative z-10 mx-auto px-6 max-w-7xl">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-4xl">
            <div className="aj-chip mb-6"><MessageSquare className="w-3 h-3" /> Contact</div>
            <h1 className="font-playfair text-4xl sm:text-5xl md:text-6xl text-white leading-[1.05]">
              Tell us how we can{" "}
              <span className="italic text-aj-gradient">help.</span>
            </h1>
            <p className="font-inter text-base md:text-lg text-white/65 mt-5 max-w-2xl leading-relaxed">
              Whether you need legal help, want to join as a lawyer, fund the Justice Wallet, or build an
              institutional partnership — we read every message.
            </p>
          </motion.div>
        </div>
      </section>

      <Section className="!pt-0">
        <div className="grid lg:grid-cols-12 gap-8">
          {/* Form */}
          <div className="lg:col-span-7">
            <GlassCard className="!p-8 lg:!p-10">
              <Eyebrow className="mb-3">Send a message</Eyebrow>
              <h2 className="font-playfair text-3xl text-white mb-8">We respond within 24 hours.</h2>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block font-montserrat text-[10px] uppercase tracking-[0.22em] text-white/55 mb-2">Full Name</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      data-testid="contact-name"
                      className="w-full bg-white/[0.04] border border-white/10 rounded-full px-5 py-3 font-inter text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-turquoise/60 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block font-montserrat text-[10px] uppercase tracking-[0.22em] text-white/55 mb-2">Email</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      data-testid="contact-email"
                      className="w-full bg-white/[0.04] border border-white/10 rounded-full px-5 py-3 font-inter text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-turquoise/60 transition-all"
                    />
                  </div>
                </div>
                <div>
                  <label className="block font-montserrat text-[10px] uppercase tracking-[0.22em] text-white/55 mb-2">Organization (optional)</label>
                  <input
                    type="text"
                    value={formData.organization}
                    onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
                    data-testid="contact-org"
                    className="w-full bg-white/[0.04] border border-white/10 rounded-full px-5 py-3 font-inter text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-turquoise/60 transition-all"
                  />
                </div>
                <div>
                  <label className="block font-montserrat text-[10px] uppercase tracking-[0.22em] text-white/55 mb-3">I&apos;m reaching out about</label>
                  <div className="flex flex-wrap gap-2" data-testid="contact-type">
                    {submissionTypes.map((s) => (
                      <button
                        key={s.v}
                        type="button"
                        onClick={() => setFormData({ ...formData, submission_type: s.v })}
                        data-testid={`contact-type-${s.v}`}
                        className={`px-4 py-2 rounded-full font-montserrat text-[10px] uppercase tracking-wider font-semibold transition-all ${
                          formData.submission_type === s.v
                            ? "bg-turquoise text-ink"
                            : "bg-white/[0.03] border border-white/10 text-white/70 hover:border-turquoise/40"
                        }`}
                      >
                        {s.l}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block font-montserrat text-[10px] uppercase tracking-[0.22em] text-white/55 mb-2">Message</label>
                  <textarea
                    rows={5}
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    required
                    data-testid="contact-message"
                    className="w-full bg-white/[0.04] border border-white/10 rounded-2xl px-5 py-3 font-inter text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-turquoise/60 transition-all resize-none"
                  />
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  data-testid="contact-submit"
                  className="btn-aj-primary disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (<><Loader2 className="w-4 h-4 animate-spin" /> Sending…</>) : (<><Send className="w-4 h-4" /> Send Message</>)}
                </button>
              </form>
            </GlassCard>
          </div>

          {/* Contact methods */}
          <aside className="lg:col-span-5 space-y-4">
            {methods.map((m, i) => (
              <motion.div
                key={m.title}
                initial={{ opacity: 0, x: 16 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: i * 0.08 }}
              >
                <GlassCard glow className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-xl bg-turquoise/10 border border-turquoise/30 flex items-center justify-center shrink-0">
                    <m.icon className="w-5 h-5 text-turquoise" />
                  </div>
                  <div>
                    <Eyebrow className="mb-1">{m.title}</Eyebrow>
                    {m.href ? (
                      <a href={m.href} target="_blank" rel="noopener noreferrer" className="font-playfair text-xl text-white hover:text-turquoise transition-colors">
                        {m.value}
                      </a>
                    ) : (
                      <div className="font-playfair text-xl text-white">{m.value}</div>
                    )}
                    <div className="font-inter text-xs text-white/45 mt-1">{m.note}</div>
                  </div>
                </GlassCard>
              </motion.div>
            ))}

            <GlassCard>
              <Eyebrow className="mb-3">Follow Us</Eyebrow>
              <div className="flex items-center gap-3">
                <a
                  href="https://www.instagram.com/accessjustice.global/"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-instagram"
                  className="w-10 h-10 rounded-full bg-white/[0.03] border border-white/10 flex items-center justify-center text-white/70 hover:text-turquoise hover:border-turquoise/40 transition-all"
                >
                  <Instagram className="w-4.5 h-4.5" />
                </a>
                <a
                  href="https://www.facebook.com/profile.php?id=61577465846664"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-facebook"
                  className="w-10 h-10 rounded-full bg-white/[0.03] border border-white/10 flex items-center justify-center text-white/70 hover:text-turquoise hover:border-turquoise/40 transition-all"
                >
                  <Facebook className="w-4.5 h-4.5" />
                </a>
                <a
                  href="https://wa.me/2348114445557"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-whatsapp"
                  className="w-10 h-10 rounded-full bg-white/[0.03] border border-white/10 flex items-center justify-center text-white/70 hover:text-turquoise hover:border-turquoise/40 transition-all"
                >
                  <Phone className="w-4.5 h-4.5" />
                </a>
              </div>
            </GlassCard>
          </aside>
        </div>
      </Section>
    </div>
  );
};

export default Contact;
